import {
  Badge,
  Banner,
  Button,
  Counter,
  IconButton,
  Metric,
  Row,
  Spinner,
  TextArea,
  Toast,
  Toolbar,
  icons,
  useEscapeLayer,
} from '@ui';
import {
  ClipboardEvent,
  DragEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from './api';
import {
  AlertIcon,
  BugIcon,
  CameraIcon,
  CheckIcon,
  ChevronIcon,
  CloseIcon,
  CropIcon,
  FileCodeIcon,
  FrameIcon,
  HelpIcon,
  MarkIcon,
  PanelCollapseIcon,
  PanelExpandIcon,
  PaperclipIcon,
  PlusIcon,
  SendIcon,
  SparkIcon,
  TraceIcon,
  TrashIcon,
  UndoIcon,
} from './icons';
import { bugApi, type BugUpload, type RephraseResult, type RephraseVariant } from './bugApi';
import { RephraseAnswer, RephraseButton, useRephrase } from './RephrasePanel';
import { AreaPicker, type AreaRect } from './AreaPicker';
import ScreenMaskBeacon from './ScreenMaskBeacon';
import { screenMaskOffered, shotCover } from './screenMask';
import { EMPTY_AREA, PANEL_TEXT_ATTR, captureViewport, composeShot, nextPaint, shotForModel, skipOnAreaShot, skipOnShot, type ShotRect } from './capture';
import { withoutBodies } from './bodies';
import { dialogConfig, pageShift } from './config';
import { blocksInView, touchedText, type ShotBlock } from './shotBlocks';
import { AREA_HOTKEY, dialogOver, isAreaShotKey } from './hotkeys';
import { maskKnown } from './known';
import { redactQuery } from '../../shared/redact';
import { fitDescription } from '../../shared/report';
import {
  BUG_OWN_ATTR,
  actionsSince,
  clearActions,
  clearQuietTrace,
  clearTrace,
  lastAction,
  noteWidgetEvent,
  quietTrace,
  stopTrace,
  traceFile,
  traceStatus,
} from './recorder';
import {
  EDITABLE_STEPS,
  REQUIRED_STEPS,
  answeredCount,
  describeScenarios,
  hasWords,
  applyRephrase,
  insertShotMarks,
  isScenarioAction,
  keepMarksWhole,
  markNumbers,
  plural,
  prefillSince,
  renumberShotMarks,
  scenarioFileName,
  shotMark,
  stepsFromActions,
  type Answers,
  type StepKey,
} from './scenario';
import { traceLabel, type TraceFile } from './trace';
import { Fold } from './ui';
import { useConfirm } from './jui';

/** What is sent: the state and page HTML cleaned with the dictionary as it is at sending. */
export interface PreparedDraft {
  clientState: Record<string, unknown>;
  pageHtml: string;
}

export interface BugReportDraft {
  id: number;
  masked: boolean;
  /** Taken at opening; without `prepare` — sent as is. */
  clientState: Record<string, unknown>;
  pageHtml: string;
  /**
   * Cleans `clientState` and the page later, in pieces (report 74, П217); the same result while the dictionary is the
   * same. `fresh: false` — one pass, whatever is learned meanwhile (the background call).
   */
  prepare?: (options?: { fresh?: boolean }) => Promise<PreparedDraft>;
  /** The draft is gone: a cleaning pass stops. */
  dispose?: () => void;
  pageUrl: string;
}

/** A question of the help panel turned into a report; `id` — its number in the tracker (null — it was not stored). */
export interface QuestionSeed {
  id: number | null;
  question: string;
  place: string;
  places: { kind: string; anchors: string[]; text: string }[];
  found: boolean;
  answer: string;
}

/** Stored questions of the draft: the first as `questionId` (as before), the rest as `questionIds`. */
export function questionIdsOf(seeds: QuestionSeed[]): {
  questionId?: number;
  questionIds?: number[];
} {
  const ids = seeds.map((seed) => seed.id).filter((id): id is number => typeof id === 'number' && id > 0);
  if (ids.length === 0) return {};
  return ids.length === 1 ? { questionId: ids[0] } : { questionId: ids[0], questionIds: ids.slice(1) };
}

/** The question's lines at the top of the report's description. */
export function questionLines(seed: QuestionSeed): string {
  return [
    `Вопрос${seed.id ? ` №${seed.id}` : ''}: ${seed.question}`,
    `Место: ${seed.place}`,
    `${seed.found ? 'Ответ (есть в коде)' : 'Ответ (предположение)'}: ${seed.answer || 'нет'}`,
  ].join('\n');
}

interface Shot {
  key: number;
  status: 'capturing' | 'ready' | 'failed';
  blob?: Blob;
  url?: string;
  own: boolean;
  rects: ShotRect[];
  masked?: boolean;
  area?: boolean;
  error?: string;
  /** The page (path and query, secrets hidden) the shot was taken on; the draft outlives page changes (report 26). */
  page?: string;
  /** Сн3: the blocks of the page tree this shot touched (mark and path), as the layout was when it began. */
  blocks?: ShotBlock[];
}

/** How a shot came in (the log of the tool, reports 105–107); no shot is taken at opening (Сн1). */
type ShotKind = 'button' | 'area' | 'file' | 'paste' | 'drop' | 'restore';

/** One try of the shot tool: what was asked, where, with what, and what came of it. */
interface ShotLogEntry {
  at: string;
  kind: ShotKind;
  scenario: number;
  page: string;
  masked: boolean;
  area?: AreaRect;
  ms?: number;
  bytes?: number;
  px?: { w: number; h: number };
  /** Blocks of the screen (mark and path) that crossed the frame — the window, for a shot of the page — as the shot began. */
  blocks?: ShotBlock[];
  result: 'ready' | 'empty' | 'failed';
  error?: string;
}

const SHOT_LOG_MAX = 100;

/** The page the panel is on: path and query with secret values hidden (the same shape the record keeps). */
const pageKey = (): string => redactQuery(`${window.location.pathname}${window.location.search}`);

interface FileItem {
  key: number;
  name: string;
  size: number;
  status: 'uploading' | 'done' | 'error';
  upload?: BugUpload;
  error?: string;
}

const MAX_ATTACHMENTS = 20;

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message : fallback;
}

function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

function extOf(type: string): string {
  if (type.includes('png')) return 'png';
  if (type.includes('webp')) return 'webp';
  if (type.includes('gif')) return 'gif';
  return 'jpg';
}




function ShotEditor({ src, rects, onChange }: { src: string; rects: ShotRect[]; onChange: (rects: ShotRect[]) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const [draft, setDraft] = useState<ShotRect | null>(null);

  const at = (event: PointerEvent) => {
    const rect = box.current!.getBoundingClientRect();
    const clamp = (value: number) => Math.min(1, Math.max(0, value));
    return {
      x: clamp((event.clientX - rect.left) / rect.width),
      y: clamp((event.clientY - rect.top) / rect.height),
    };
  };

  const toRect = (a: { x: number; y: number }, b: { x: number; y: number }): ShotRect => ({
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  });

  return (
    <div
      ref={box}
      className="bug-shot"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        origin.current = at(event);
        setDraft(toRect(origin.current, origin.current));
      }}
      onPointerMove={(event) => {
        if (origin.current) setDraft(toRect(origin.current, at(event)));
      }}
      onPointerUp={(event) => {
        if (!origin.current) return;
        const rect = toRect(origin.current, at(event));
        origin.current = null;
        setDraft(null);
        if (rect.w > 0.01 && rect.h > 0.01) onChange([...rects, rect]);
      }}
      onPointerCancel={() => {
        origin.current = null;
        setDraft(null);
      }}
    >
      <img src={src} alt="Снимок экрана" draggable={false} />
      {[...rects, ...(draft ? [draft] : [])].map((r, i) => (
        <span
          key={i}
          className="bug-shot-rect"
          style={{
            left: `${r.x * 100}%`,
            top: `${r.y * 100}%`,
            width: `${r.w * 100}%`,
            height: `${r.h * 100}%`,
          }}
        />
      ))}
    </div>
  );
}

const OWN = { [BUG_OWN_ATTR]: '' };
const FIELD_ATTR = 'data-bug-field';

interface MarkAt {
  scenario: number;
  field: FieldKey;
  /** `end` — after the field's text (the image came in with the caret outside the fields). */
  pos: number | 'end';
  el: HTMLTextAreaElement | null;
}

/** A field of the panel under the event: where "[снимок N]" goes. */
function fieldOf(target: EventTarget | null): HTMLTextAreaElement | null {
  return target instanceof HTMLTextAreaElement && target.hasAttribute(FIELD_ATTR) ? target : null;
}

/** Text offset under the pointer inside a textarea (Firefox, Chrome 128+); otherwise the caret, else the end. */
function dropOffset(el: HTMLTextAreaElement, x: number, y: number): number {
  const at = (
    document as Document & {
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    }
  ).caretPositionFromPoint?.(x, y);
  if (at && at.offsetNode === el) return at.offset;
  return document.activeElement === el ? el.selectionStart : el.value.length;
}
/** Kept for what the panel adds itself: the page HTML and the quiet mouse trace. */
const RESERVED = 2;

/** A removed shot under «Вернуть»: where it stood and where its mark was (it comes back there). */
interface Removed {
  scenario: number;
  shot: Shot;
  index: number;
  mark: { field: FieldKey; pos: number } | null;
}

/** Where mark `n` stands in a scenario's fields (the first one of possibly several), null — nowhere. */
function findMark(answers: Answers, n: number): { field: FieldKey; pos: number } | null {
  for (const field of ['expected', 'guess'] as const) {
    const pos = answers[field].indexOf(shotMark(n));
    if (pos >= 0) return { field, pos };
  }
  return null;
}

interface ScenarioItem {
  key: number;
  answers: Answers;
  autoDid: string;
  shots: Shot[];
  traces: TraceFile[];
  createdAt: string;
  applied: Partial<Answers>;
}

type Section = 'file';
type FieldKey = Exclude<StepKey, 'did'>;

const SHORT: Record<FieldKey, string> = {
  expected: 'Ждал',
  guess: 'Предполагаемые причина и решение',
};
const SCENARIOS: [string, string, string] = ['сценарий', 'сценария', 'сценариев'];
const SHOTS: [string, string, string] = ['снимок', 'снимка', 'снимков'];

const WIDTH_KEY = 'bugPanelWidth';
const MIN_WIDTH = 360;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 420;
const APP_ROOM = 360;
const UNDO_MS = 10_000;

/** Only the width number is stored: nothing of the report content (it may carry people's names). */
function readWidth(): number {
  try {
    const value = Number(window.localStorage.getItem(WIDTH_KEY));
    if (value >= MIN_WIDTH && value <= MAX_WIDTH) return value;
  } catch {}
  return DEFAULT_WIDTH;
}

function saveWidth(value: number): void {
  try {
    window.localStorage.setItem(WIDTH_KEY, String(Math.round(value)));
  } catch {}
}

const narrow = (): boolean => window.innerWidth <= 600;

const clampWidth = (value: number): number => {
  const room = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - APP_ROOM));
  return Math.round(Math.min(room, Math.max(MIN_WIDTH, value)));
};

/** The running record's counter re-renders only itself: the whole panel used to re-render twice a second for it. */
function LiveTrace({ children }: { children: (status: { events: number; durationMs: number }) => ReactNode }) {
  const [status, setStatus] = useState(traceStatus);
  useEffect(() => {
    const timer = window.setInterval(() => setStatus(traceStatus()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return <>{children(status ?? { events: 0, durationMs: 0 })}</>;
}

export interface BugReportDialogProps {
  draft: BugReportDraft;
  minimized: boolean;
  onMinimize: () => void;
  onRestore: () => void;
  onClose: () => void;
  onSent: () => void;
  onAnother: () => void;
  /** Upload cap from the service config (`limits.maxBodyMb`); 8 MB when it does not say. */
  maxMb?: number;
  /**
   * «Сделать репортом» from questions: their places and answers head the report. Kept apart from `draft` (a new draft
   * object would restart the dialog's shot): a later question joins the open draft without touching what is in it.
   */
  questions?: QuestionSeed[];
}

export default function BugReportDialog({
  draft,
  minimized,
  onMinimize,
  onRestore,
  onClose,
  onSent,
  onAnother,
  maxMb = 8,
  questions,
}: BugReportDialogProps) {
  const keySeq = useRef(1);
  const openedAt = useMemo(() => new Date().toISOString(), [draft]);
  const firstDid = useMemo(() => stepsFromActions(draft.clientState.actions as { type: string; target?: string; own?: boolean }[] | undefined), [draft]);
  // Сн1: nothing is shot at opening — scenario 1 starts with no shots and the person makes the first one.
  const [scenarios, setScenarios] = useState<ScenarioItem[]>(() => [
    {
      key: 1,
      answers: {
        did: firstDid,
        expected: '',
        guess: '',
      },
      autoDid: firstDid,
      shots: [],
      traces: [],
      createdAt: openedAt,
      applied: {},
    },
  ]);
  const [activeKey, setActiveKey] = useState(1);
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());
  const [removed, setRemoved] = useState<Removed | null>(null);
  /** The log of the shot tool (reports 105–107): every try, sent in `clientState.shotLog`, names hidden at sending. */
  const shotLog = useRef<ShotLogEntry[]>([]);
  const logShot = (entry: Omit<ShotLogEntry, 'at'>) => {
    shotLog.current.push({ at: new Date().toISOString(), ...entry });
    if (shotLog.current.length > SHOT_LOG_MAX) shotLog.current.splice(0, shotLog.current.length - SHOT_LOG_MAX);
  };
  const scenarioNumber = (key: number): number => Math.max(1, scenarios.findIndex((item) => item.key === key) + 1);
  const [viewer, setViewer] = useState<{
    scenario: number;
    shot: number;
  } | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [picking, setPicking] = useState(false);
  /** The scenario whose area button started the pick: the frame shot lands there. */
  const areaOwner = useRef<number | null>(null);
  const [shotError, setShotError] = useState('');
  const [sections, setSections] = useState<Set<Section>>(() => new Set());
  const [files, setFiles] = useState<FileItem[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentId, setSentId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  // Report 179 (Quint facts report_panel П341): the page HTML and response bodies go with every report — no switch.
  const [trace, setTrace] = useState(traceStatus);
  const [width, setWidth] = useState(readWidth);
  const [resizing, setResizing] = useState(false);
  const traceOwner = useRef<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const shotInput = useRef<HTMLInputElement>(null);
  const firstField = useRef<HTMLTextAreaElement | null>(null);
  /** The field the caret was last in: a shot taken with a button or a frame is marked at its caret. */
  const lastField = useRef<HTMLTextAreaElement | null>(null);
  const urls = useRef<string[]>([]);

  const options = dialogConfig();
  const filesOn = options.files !== false;
  // Without the switch the page HTML and response bodies always go.
  const withBodies = true;
  const active = scenarios.find((item) => item.key === activeKey) ?? scenarios[scenarios.length - 1];
  const activeRef = useRef(active.key);
  activeRef.current = active.key;

  const updateScenario = (key: number, change: (item: ScenarioItem) => ScenarioItem) =>
    setScenarios((prev) => prev.map((item) => (item.key === key ? change(item) : item)));
  const mapShots = (change: (shot: Shot) => Shot) => setScenarios((prev) => prev.map((item) => ({ ...item, shots: item.shots.map(change) })));

  const objectUrl = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    urls.current.push(url);
    return url;
  };

  useEffect(() => {
    const first = firstField.current;
    if (first) {
      first.focus();
      first.setSelectionRange(first.value.length, first.value.length);
      lastField.current = first;
    }
    return () => {
      urls.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [draft]);

  useEffect(() => {
    if (!removed) return;
    const timer = window.setTimeout(() => setRemoved(null), UNDO_MS);
    return () => window.clearTimeout(timer);
  }, [removed]);

  // The page makes room for the panel (`html.bug-panel-open`, `--bug-w`) at once (report 74: it used to wait for the
  // first shot, the panel covering the page meanwhile); the first shot is of the page beside the panel, cut at its
  // edge, like the panel's own shots. On a narrow screen the full-width panel hides for a shot instead.
  const hidden = minimized || (capturing && narrow());
  const shifted = !hidden;
  useEffect(() => {
    document.documentElement.classList.toggle('bug-panel-open', shifted);
    return () => document.documentElement.classList.remove('bug-panel-open');
  }, [shifted]);

  useEffect(() => {
    const apply = () => document.documentElement.style.setProperty('--bug-w', `${clampWidth(width)}px`);
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [width]);
  useEffect(
    () => () => {
      document.documentElement.style.removeProperty('--bug-w');
    },
    [],
  );

  useEffect(() => {
    const fit = () => document.documentElement.style.setProperty('--bug-vw', `${Math.round(window.visualViewport?.width ?? window.innerWidth)}px`);
    fit();
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      document.documentElement.style.removeProperty('--bug-vw');
    };
  }, []);

  // Esc closes only the shot viewer (the topmost layer, П306), never the panel: the panel holds the draft.
  useEscapeLayer(!!viewer, () => setViewer(null));
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [toastBox, setToastBox] = useState<HTMLDivElement | null>(null);

  const startResize = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setResizing(true);
  };
  const moveResize = (event: PointerEvent<HTMLDivElement>) => {
    if (resizing) setWidth(clampWidth(window.innerWidth - event.clientX));
  };
  const endResize = () => {
    if (!resizing) return;
    setResizing(false);
    saveWidth(width);
  };
  const keyResize = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 64 : 16;
    const next =
      event.key === 'ArrowLeft'
        ? width + step
        : event.key === 'ArrowRight'
          ? width - step
          : event.key === 'Home'
            ? MIN_WIDTH
            : event.key === 'End'
              ? MAX_WIDTH
              : null;
    if (next === null) return;
    event.preventDefault();
    const value = clampWidth(next);
    setWidth(value);
    saveWidth(value);
  };

  const attached = files.filter((f) => f.status !== 'error').length;
  const allShots = scenarios.flatMap((item) => item.shots);
  const readyCount = allShots.filter((shot) => shot.status === 'ready' && shot.blob).length;
  const traceCount = scenarios.reduce((sum, item) => sum + item.traces.length, 0) + (trace ? 1 : 0);

  const roomFor = (count: number): boolean => {
    if (allShots.length + attached + traceCount + RESERVED + count <= MAX_ATTACHMENTS) return true;
    setShotError(`Не больше ${MAX_ATTACHMENTS} вложений`);
    return false;
  };

  const addImage = (blob: Blob, kind: ShotKind = 'file') => addImages([blob], undefined, kind);

  /** The caret goes right after what was put in `at`, once the field shows it (`caret` is read then: the updater runs at render). */
  const caretAfter = (at: MarkAt, caret: () => number | null) => {
    window.requestAnimationFrame(() => {
      const pos = caret();
      if (pos === null || !at.el?.isConnected) return;
      at.el.focus();
      at.el.setSelectionRange(pos, pos);
    });
  };

  /**
   * Every shot — pasted, dropped, picked, taken with a button or a frame — joins its scenario together with its mark
   * (П214): numbers are taken inside the same update, so two shots in a row never get the same number or lose one.
   */
  const addShots = (owner: number, added: Shot[], at?: MarkAt) => {
    let caret: number | null = null;
    updateScenario(owner, (item) => {
      const numbers = added.map((_, i) => item.shots.length + i + 1);
      const shots = [...item.shots, ...added];
      if (!at || at.scenario !== owner) return { ...item, shots };
      const text = item.answers[at.field];
      const marked = insertShotMarks(text, at.pos === 'end' ? text.length : at.pos, numbers);
      caret = marked.caret;
      return {
        ...item,
        shots,
        answers: { ...item.answers, [at.field]: marked.text },
      };
    });
    if (at) caretAfter(at, () => caret);
  };

  /**
   * Report 105: «[снимок N]» once more — the mark of a shot already in the scenario goes in at the caret of the field
   * last typed in (П214 reads «not less than one mark» since: ✕ takes every mark of the number, «Вернуть» brings one).
   */
  const markAgain = (owner: number, n: number) => {
    const at = markAt(null, () => 0, owner);
    if (!at) return;
    let caret: number | null = null;
    updateScenario(owner, (item) => {
      const text = item.answers[at.field];
      const marked = insertShotMarks(text, at.pos === 'end' ? text.length : at.pos, [n]);
      caret = marked.caret;
      return { ...item, answers: { ...item.answers, [at.field]: marked.text } };
    });
    caretAfter(at, () => caret);
    noteWidgetEvent(`метка снимка ${n} в текст`);
  };

  /** Own images go to the scenario of the field they were pasted / dropped on (else the current one), marked there. */
  const addImages = (blobs: Blob[], at?: MarkAt, kind: ShotKind = 'file') => {
    if (blobs.length === 0 || !roomFor(blobs.length)) return;
    setShotError('');
    const owner = at?.scenario ?? activeRef.current;
    const page = pageKey();
    const added: Shot[] = blobs.map((blob) => ({
      key: (keySeq.current += 1),
      status: 'ready',
      blob,
      url: objectUrl(blob),
      own: true,
      rects: [],
      page,
    }));
    for (const blob of blobs)
      logShot({
        kind,
        scenario: scenarioNumber(owner),
        page,
        masked: false,
        bytes: blob.size,
        result: 'ready',
      });
    addShots(owner, added, at);
  };

  /** Marks of a scenario follow its shots: `map` takes a thumbnail number, null drops its mark. */
  const remark = (item: ScenarioItem, map: (n: number) => number | null): ScenarioItem =>
    options.inlineShotMarks
      ? {
          ...item,
          answers: {
            ...item.answers,
            expected: renumberShotMarks(item.answers.expected, map),
            guess: renumberShotMarks(item.answers.guess, map),
          },
        }
      : item;

  /** Width the page actually gave up for the panel (Хс3): a plain shot is cut by it (a frame takes what it covers). */
  const panelCut = (area?: AreaRect): number => (!area && !minimized ? pageShift() : 0);

  /**
   * A shot of the page for `owner` (report 84: the tools sit in a scenario's shots block); the pill shoots the current one.
   * A frame (reports 103 / 106) takes what it covers, the panel included; a frame over nothing fails as «empty».
   */
  const addCapture = async (area?: AreaRect, owner = activeRef.current) => {
    if (capturing || !roomFor(1)) return;
    setShotError('');
    const key = (keySeq.current += 1);
    // Marked names are hidden on every shot (Мс3) — unless the host gave the switch and the person turned the tools row's
    // shield off (Щт1, Quint shot_shield). The shield covers the shots, not the page (Ок6, Quint report_tools). The choice is
    // read as the shot begins: shots already taken keep what they were (Щт3).
    const { hideMarked: masked, coverScreen } = shotCover(options.maskSwitch && screenMaskOffered());
    const page = pageKey();
    const entry = {
      kind: area ? ('area' as const) : ('button' as const),
      scenario: scenarioNumber(owner),
      page,
      masked,
      ...(area ? { area } : {}),
    };
    setCapturing(true);
    addShots(
      owner,
      [
        {
          key,
          status: 'capturing',
          own: false,
          rects: [],
          masked,
          page,
          ...(area ? { area: true } : {}),
        },
      ],
      markAt(null, () => 0, owner),
    );
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(owner);
      return next;
    });
    await nextPaint();
    let blocks: ShotBlock[] = [];
    try {
      // The blocks in the frame, by the layout as the shot begins.
      blocks = blocksInView(area, panelCut(area));
      const taken = await captureViewport(area ? skipOnAreaShot : skipOnShot, {
        hideNames: masked,
        coverScreen,
        cropRight: panelCut(area),
        area,
      });
      logShot({
        ...entry,
        blocks,
        ms: taken.ms,
        bytes: taken.blob.size,
        px: taken.px,
        result: 'ready',
      });
      mapShots((shot) =>
        shot.key === key
          ? {
              ...shot,
              status: 'ready',
              blob: taken.blob,
              url: objectUrl(taken.blob),
              blocks,
            }
          : shot,
      );
    } catch (err) {
      const error = errorText(err, 'неизвестная ошибка');
      logShot({
        ...entry,
        blocks,
        result: error === EMPTY_AREA ? 'empty' : 'failed',
        error,
      });
      mapShots((shot) => (shot.key === key ? { ...shot, status: 'failed', error } : shot));
    } finally {
      setCapturing(false);
    }
  };

  const startArea = (owner = activeRef.current) => {
    if (capturing || picking || !roomFor(1)) return;
    setShotError('');
    areaOwner.current = owner;
    setPicking(true);
    noteWidgetEvent('рамка: старт');
  };
  const pickArea = (rect: AreaRect) => {
    setPicking(false);
    noteWidgetEvent(`рамка: ${Math.round(rect.w)}×${Math.round(rect.h)} в (${Math.round(rect.x)}, ${Math.round(rect.y)})`);
    void addCapture(rect, areaOwner.current ?? activeRef.current);
  };
  const cancelArea = useCallback(() => {
    setPicking(false);
    noteWidgetEvent('рамка: отмена');
  }, []);

  // Report 107: Alt+Shift+S — the frame, while the panel is open (inside the fields too).
  const keys = useRef<{ area: () => void }>({ area: () => undefined });
  keys.current = { area: () => startArea() };
  useEffect(() => {
    if (minimized || sentId !== null) return;
    const onKey = (event: KeyboardEvent) => {
      if (!isAreaShotKey(event) || dialogOver()) return;
      event.preventDefault();
      event.stopPropagation();
      keys.current.area();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [minimized, sentId]);

  const removeShot = (scenarioKey: number, key: number) => {
    const item = scenarios.find((s) => s.key === scenarioKey);
    const index = item?.shots.findIndex((shot) => shot.key === key) ?? -1;
    if (!item || index < 0) return;
    const gone = index + 1;
    // Every mark of the number goes (report 105: a shot may stand in the text more than once); «Вернуть» brings one.
    setRemoved({
      scenario: scenarioKey,
      shot: item.shots[index],
      index,
      mark: options.inlineShotMarks ? findMark(item.answers, gone) : null,
    });
    updateScenario(scenarioKey, (s) => remark({ ...s, shots: s.shots.filter((shot) => shot.key !== key) }, (n) => (n === gone ? null : n > gone ? n - 1 : n)));
    noteWidgetEvent(`снимок ${gone} убран (сценарий ${scenarioNumber(scenarioKey)})`);
    if (viewer?.shot === key) {
      const rest = item.shots.filter((shot) => shot.key !== key && shot.status === 'ready');
      const next = rest[Math.min(index, rest.length - 1)];
      setViewer(next ? { scenario: scenarioKey, shot: next.key } : null);
    }
  };

  const restoreShot = () => {
    if (!removed) return;
    const target = scenarios.some((s) => s.key === removed.scenario) ? removed.scenario : active.key;
    const mark = target === removed.scenario ? removed.mark : null;
    updateScenario(target, (s) => {
      const next = [...s.shots];
      const at = Math.min(removed.index, next.length);
      next.splice(at, 0, removed.shot);
      const shifted = remark({ ...s, shots: next }, (n) => (n > at ? n + 1 : n));
      // П214: the restored shot gets its mark back where it stood.
      if (!mark) return shifted;
      const text = shifted.answers[mark.field];
      return {
        ...shifted,
        answers: {
          ...shifted.answers,
          [mark.field]: insertShotMarks(text, Math.min(mark.pos, text.length), [at + 1]).text,
        },
      };
    });
    const shot = removed.shot;
    logShot({
      kind: 'restore',
      scenario: scenarioNumber(target),
      page: shot.page ?? pageKey(),
      masked: !shot.own && shot.masked !== false,
      ...(shot.blob ? { bytes: shot.blob.size } : {}),
      result: shot.status === 'ready' ? 'ready' : 'failed',
      ...(shot.error ? { error: shot.error } : {}),
    });
    noteWidgetEvent(`снимок ${removed.index + 1} возвращён`);
    setRemoved(null);
  };

  const setRects = (scenarioKey: number, shotKey: number, rects: ShotRect[]) =>
    updateScenario(scenarioKey, (s) => ({
      ...s,
      shots: s.shots.map((shot) => (shot.key === shotKey ? { ...shot, rects } : shot)),
    }));

  /**
   * Typed text goes in as is, except where it cut into a «[снимок N]» mark (report 95): the mark stays one piece, and
   * the marks stay the shots (Мт2, Мт3, Quint shot_marks): a mark the edit took out of the text takes its shot with it
   * («Вернуть» brings the shot and its mark back), a mark typed by hand for a shot that is not there is not kept, and
   * the numbers left follow the thumbnails.
   */
  const setAnswer = (scenarioKey: number, key: FieldKey, text: string, el: HTMLTextAreaElement) => {
    const item = scenarios.find((s) => s.key === scenarioKey);
    // The field shows the rendered state: that is what the edit was made on.
    const shown = item?.answers[key] ?? '';
    const whole = options.inlineShotMarks ? keepMarksWhole(shown, text, el.selectionStart) : null;
    const typed = whole ? whole.text : text;
    if (!item || !options.inlineShotMarks) {
      updateScenario(scenarioKey, (s) => ({ ...s, answers: { ...s.answers, [key]: typed } }));
    } else {
      const count = item.shots.length;
      const kept = renumberShotMarks(typed, (n) => (n >= 1 && n <= count ? n : null));
      const next: Answers = { ...item.answers, [key]: kept };
      const before = markNumbers(item.answers.expected, item.answers.guess);
      const after = markNumbers(next.expected, next.guess);
      const gone = [...before].filter((n) => !after.has(n) && n >= 1 && n <= count).sort((a, b) => a - b);
      if (gone.length === 0) {
        updateScenario(scenarioKey, (s) => ({ ...s, answers: { ...s.answers, [key]: kept } }));
      } else {
        const last = gone[gone.length - 1];
        setRemoved({ scenario: scenarioKey, shot: item.shots[last - 1], index: last - 1, mark: findMark(item.answers, last) });
        noteWidgetEvent(`снимки ${gone.join(', ')} убраны вместе с меткой (сценарий ${scenarioNumber(scenarioKey)})`);
        updateScenario(scenarioKey, (s) =>
          remark(
            { ...s, answers: { ...s.answers, [key]: kept }, shots: s.shots.filter((_, i) => !gone.includes(i + 1)) },
            (n) => (gone.includes(n) ? null : n - gone.filter((g) => g < n).length),
          ),
        );
        if (viewer && item.shots.some((shot, i) => shot.key === viewer.shot && gone.includes(i + 1))) setViewer(null);
      }
    }
    if (!whole) return;
    window.requestAnimationFrame(() => {
      if (el.isConnected) el.setSelectionRange(whole.caret, whole.caret);
    });
  };

  const takeLiveTrace = (): { owner: number; file: TraceFile } | null => {
    if (!traceStatus()) return null;
    stopTrace();
    const file = traceFile();
    const owner = traceOwner.current;
    clearTrace();
    traceOwner.current = null;
    setTrace(null);
    if (!file || owner === null) return null;
    updateScenario(owner, (s) => ({ ...s, traces: [...s.traces, file] }));
    return { owner, file };
  };

  /** The switch of a scenario: stops its own recording; another scenario's recording is taken and a new one starts here. */

  // Report 179 (Quint facts report_panel П342): «по действиям» keeps up with what the person does on the page while the
  // panel is open — each scenario that still has its own words untouched gets the actions since it began.
  useEffect(() => {
    if (sentId !== null) return undefined;
    const before = draft.clientState.actions as { type: string; target?: string; own?: boolean }[] | undefined;
    const timer = window.setInterval(() => {
      setScenarios((list) => {
        let changed = false;
        const next = list.map((item, index) => {
          if (item.answers.did !== item.autoDid) return item;
          const did =
            index === 0
              ? stepsFromActions([...(before ?? []), ...actionsSince(openedAt).filter((action) => isScenarioAction(action))])
              : prefillSince(actionsSince(list[index - 1].createdAt).filter((action) => isScenarioAction(action)), list[index - 1].createdAt);
          if (did === item.autoDid) return item;
          changed = true;
          return { ...item, autoDid: did, answers: { ...item.answers, did } };
        });
        return changed ? next : list;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [draft, openedAt, sentId]);


  const scenarioDirty = (s: ScenarioItem): boolean =>
    answeredCount(s.answers) > 0 || s.shots.some((shot) => shot.own || shot.rects.length > 0) || s.traces.length > 0 || traceOwner.current === s.key;

  const removeScenario = async (key: number) => {
    const item = scenarios.find((s) => s.key === key);
    if (!item || scenarios.length < 2) return;
    if (
      scenarioDirty(item) &&
      !(await confirm({
        title: 'Убрать сценарий?',
        text: 'Вместе с его снимками и записью.',
        confirmLabel: 'Убрать',
      }))
    )
      return;
    if (traceOwner.current === key) {
      clearTrace();
      traceOwner.current = null;
      setTrace(null);
    }
    const rest = scenarios.filter((s) => s.key !== key);
    setScenarios(rest);
    if (viewer?.scenario === key) setViewer(null);
    if (activeKey === key) setActiveKey(rest[rest.length - 1].key);
  };

  const toggleOpen = (key: number) => {
    const next = new Set(collapsed);
    if (next.has(key)) {
      next.delete(key);
      setActiveKey(key);
    } else next.add(key);
    setCollapsed(next);
  };

  const toggleSection = (section: Section) =>
    setSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  const addFiles = (list: File[]) => {
    const room = MAX_ATTACHMENTS - RESERVED - allShots.length - traceCount - attached;
    setSections((prev) => new Set(prev).add('file'));
    for (const [index, file] of list.entries()) {
      const key = (keySeq.current += 1);
      if (index >= room) {
        setFiles((prev) => [
          ...prev,
          {
            key,
            name: file.name,
            size: file.size,
            status: 'error',
            error: `не больше ${MAX_ATTACHMENTS} вложений`,
          },
        ]);
        continue;
      }
      if (file.size > maxMb * 1024 * 1024) {
        setFiles((prev) => [
          ...prev,
          {
            key,
            name: file.name,
            size: file.size,
            status: 'error',
            error: `больше ${maxMb} МБ`,
          },
        ]);
        continue;
      }
      setFiles((prev) => [...prev, { key, name: file.name, size: file.size, status: 'uploading' }]);
      bugApi
        .upload('file', file.name, file)
        .then(({ upload }) => setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, status: 'done', upload } : f))))
        .catch((err) =>
          setFiles((prev) =>
            prev.map((f) =>
              f.key === key
                ? {
                    ...f,
                    status: 'error',
                    error: errorText(err, 'не загрузился'),
                  }
                : f,
            ),
          ),
        );
    }
  };

  const busyUploads = files.some((f) => f.status === 'uploading');
  const dirty =
    scenarios.length > 1 ||
    scenarios.some(scenarioDirty) ||
    files.length > 0 ||
    allShots.length > 1 ||
    !!trace;

  const discardUploads = () => {
    for (const item of files) if (item.upload) void bugApi.discard(item.upload.id).catch(() => undefined);
  };

  useEffect(() => () => clearTrace(), []);

  const close = async () => {
    if (
      sentId === null &&
      dirty &&
      !(await confirm({
        title: 'Закрыть без отправки?',
        text: 'Сценарии, снимки и вложения пропадут.',
        confirmLabel: 'Закрыть',
      }))
    )
      return;
    if (sentId === null) discardUploads();
    clearTrace();
    onClose();
  };

  const asTaken = useMemo<PreparedDraft>(() => ({ clientState: draft.clientState, pageHtml: draft.pageHtml }), [draft]);
  const prepareDraft = useCallback((): Promise<PreparedDraft> => (draft.prepare ? draft.prepare() : Promise.resolve(asTaken)), [draft, asTaken]);
  // The cleaning goes once the first shot is done, while the person types; sending only finishes it.
  useEffect(() => {
    let timer = 0;
    let alive = true;
    const later = () => {
      if (alive) timer = window.setTimeout(() => void draft.prepare?.({ fresh: false }).catch(() => undefined), 300);
    };
    later();
    return () => {
      alive = false;
      window.clearTimeout(timer);
      draft.dispose?.();
    };
  }, [draft]);
  // Only ready shots are sent (`sN-shotK`, K counts them): a mark of a failed or pending one goes, the rest follow K.
  const sentAnswers = (item: ScenarioItem): Answers => {
    if (!options.inlineShotMarks) return item.answers;
    const sentNumber = new Map<number, number>();
    let k = 0;
    item.shots.forEach((shot, i) => {
      if (shot.status === 'ready' && shot.blob) sentNumber.set(i + 1, (k += 1));
    });
    return remark(item, (n) => sentNumber.get(n) ?? null).answers;
  };
  const seeds = questions ?? [];
  /**
   * П224: the package's texts — the auto «Что делали» (built from the record at opening, masked as of each click) and
   * what the person typed — pass the dictionary as it is at the moment of transfer, like the record and the page: a name
   * learned while the shots and the page were uploading does not leave in the description or `scenarios[].did`.
   */
  const cleanAnswers = (answers: Answers): Answers => ({
    did: maskKnown(answers.did),
    expected: maskKnown(answers.expected),
    guess: maskKnown(answers.guess),
  });
  const descriptionOf = (answers: Answers[]): string =>
    [
      ...seeds.map(questionLines),
      describeScenarios(answers, []),
    ]
      .filter(Boolean)
      .join('\n\n');

  /** П224: the log goes with names hidden as of the moment of transfer (its page and error texts). */
  const cleanShotLog = (): ShotLogEntry[] =>
    shotLog.current.map((entry) => ({
      ...entry,
      page: maskKnown(entry.page),
      ...(entry.blocks ? { blocks: entry.blocks.map((b) => ({ block: maskKnown(b.block), path: maskKnown(b.path) })) } : {}),
      ...(entry.error ? { error: maskKnown(entry.error) } : {}),
    }));

  const stateNow = (prepared: PreparedDraft): Record<string, unknown> => {
    const clientState = withBodies ? prepared.clientState : withoutBodies(prepared.clientState);
    const during = actionsSince(openedAt).filter((action) => isScenarioAction(action));
    return {
      ...clientState,
      ...(during.length > 0 ? { actionsDuringReport: during } : {}),
      shotLog: cleanShotLog(),
    };
  };



  /**
   * «Переформулировать» (Рф1, Рф2, Рф4): everything the draft has goes to the model cleaned as for sending — texts and the
   * snapshot with the dictionary as it is now, the shots with frames, those the report carries — and the rejected variants.
   */
  const askRephrase = async (key: number, round: number, rejected: string[]): Promise<RephraseResult> => {
    const item = scenarios.find((s) => s.key === key) ?? active;
    const prepared = await prepareDraft();
    const state = stateNow(prepared);
    const last = (value: unknown, n: number) => (Array.isArray(value) ? value.slice(-n) : []);
    const ready = item.shots.filter((shot) => shot.status === 'ready' && shot.blob);
    const shots = await Promise.all(
      ready.slice(0, 4).map(async (shot, i) => ({
        label: `снимок ${i + 1}, метка «${shotMark(i + 1)}»${shot.rects.length ? `, красных рамок: ${shot.rects.length}` : ''}`,
        data: await shotForModel(shot.blob!, shot.rects),
      })),
    );
    const sent = scenarios.map((s) => cleanAnswers(sentAnswers(s)));
    const activeIdx = Math.max(0, scenarios.indexOf(item));
    return bugApi.rephrase({
      pageUrl: redactQuery(maskKnown(draft.pageUrl)),
      clientState: {
        page: state.page,
        actions: last(state.actions, 40),
        network: last(state.network, 40),
        failedNetworkEarlier: last(state.failedNetworkEarlier, 10),
        console: last(state.console, 20),
      },
      answers: sent[activeIdx],
      scenarios: sent,
      active: activeIdx,
      shots,
      rejected,
      round,
    });
  };

  /** A picked variant goes into the fields of its scenario — the only way the panel changes them (Рф5). */
  const pickRephrase = (key: number, variant: RephraseVariant) => {
    updateScenario(key, (s) => {
      const readyAt = s.shots.flatMap((shot, i) => (shot.status === 'ready' && shot.blob ? [i + 1] : []));
      return { ...s, answers: applyRephrase(s.answers, variant, readyAt, s.shots.length, !!options.inlineShotMarks) };
    });
  };

  // Рф8–Рф10: one state of the request; the button is in the footer, the answer under the fields of the scenario asked about.
  const rephrase = useRephrase({ ask: askRephrase, onPick: pickRephrase });

  const submit = async () => {
    setSending(true);
    setError('');
    try {
      const live = takeLiveTrace();
      // Report 39: without a recording of its own the report takes the quiet trace (clicks and rough moves since the
      // previous report) as the first scenario's.
      const explicit = !!live || scenarios.some((item) => item.traces.length > 0);
      const { file: quietFile, mark: quietUpTo } = quietTrace();
      const quiet = explicit ? null : quietFile;
      const ids: number[] = [];
      const perScenario: Record<string, unknown>[] = [];
      const marks: ShotRect[][] = [];
      const maskedShots: boolean[] = [];
      const areaShots: boolean[] = [];
      let own = 0;
      let traceEvents = 0;
      let traceMs = 0;
      for (const [index, item] of scenarios.entries()) {
        const n = index + 1;
        const ready = item.shots.filter((shot) => shot.status === 'ready' && shot.blob);
        for (const [i, shot] of ready.entries()) {
          const image = await composeShot(shot.blob!, shot.rects);
          const { upload } = await bugApi.upload('screenshot', scenarioFileName(n, 'shot', i + 1, extOf(image.type)), image);
          ids.push(upload.id);
        }
        const traces = [...item.traces, ...(live && live.owner === item.key ? [live.file] : []), ...(quiet && index === 0 ? [quiet] : [])];
        const sentTraces: { events: number; durationMs: number }[] = [];
        for (const file of traces) {
          const blob = new Blob([JSON.stringify(file)], {
            type: 'application/json',
          });
          if (blob.size > maxMb * 1024 * 1024) continue;
          const { upload } = await bugApi.upload('file', scenarioFileName(n, 'trace', sentTraces.length + 1, 'json'), blob);
          ids.push(upload.id);
          sentTraces.push({
            events: file.events.length,
            durationMs: file.durationMs,
          });
          traceEvents += file.events.length;
          traceMs += file.durationMs;
        }
        marks.push(...ready.map((shot) => shot.rects));
        maskedShots.push(...ready.map((shot) => !shot.own && shot.masked !== false));
        areaShots.push(...ready.map((shot) => shot.area === true));
        own += ready.filter((shot) => shot.own).length;
        const answers = cleanAnswers(sentAnswers(item));
        perScenario.push({
          n,
          did: answers.did.trim(),
          expected: answers.expected.trim(),
          guess: answers.guess.trim(),
          shots: ready.length,
          marks: ready.map((shot) => shot.rects),
          traces: sentTraces,
          since: item.createdAt,
        });
      }
      // П217: the page and the state go cleaned with the dictionary as it is now; a name learned during the page upload
      // cleans and uploads it again.
      let prepared = await prepareDraft();
      let pageId: number | null = null;
      for (;;) {
        const page = withBodies ? new Blob([prepared.pageHtml], { type: 'text/html' }) : null;
        pageId = page && page.size <= maxMb * 1024 * 1024 ? (await bugApi.upload('page', 'page.html', page)).upload.id : null;
        const now = await prepareDraft();
        if (now === prepared) break;
        if (pageId !== null) void bugApi.discard(pageId).catch(() => undefined);
        prepared = now;
      }
      if (pageId !== null) ids.push(pageId);
      for (const item of files) if (item.upload) ids.push(item.upload.id);
      // stateNow() below takes the actions up to here; what is done while the request runs is the next report's.
      const upTo = lastAction();
      const { report } = await bugApi.submit({
        description: fitDescription(descriptionOf(scenarios.map((s) => cleanAnswers(sentAnswers(s)))).trim()),
        pageUrl: draft.pageUrl,
        clientState: {
          ...stateNow(prepared),
          scenarios: perScenario,
          screenshot: {
            count: marks.length,
            own,
            marks,
            maskedNames: maskedShots,
            ...(areaShots.some(Boolean) ? { area: areaShots } : {}),
          },
          ...(traceEvents > 0 ? { trace: { events: traceEvents, durationMs: traceMs } } : {}),
          ...(seeds.length > 0 ? { fromQuestion: seeds[0] } : {}),
          ...(seeds.length > 1 ? { fromQuestions: seeds } : {}),
        },
        attachmentIds: ids,
        ...questionIdsOf(seeds),
      });
      clearTrace();
      clearActions(upTo);
      clearQuietTrace(quietUpTo);
      setSentId(report.number ?? report.id);
      onSent();
    } catch (err) {
      setError(errorText(err, 'Не удалось отправить баг-репорт'));
    } finally {
      setSending(false);
    }
  };

  /**
   * A field under the event takes the mark at `pos`; otherwise the caret of the field last focused in the current
   * scenario; otherwise it goes after "Ждал" of the current scenario.
   */
  const markAt = (el: HTMLTextAreaElement | null, pos: (el: HTMLTextAreaElement) => number, owner = activeRef.current): MarkAt | undefined => {
    if (!options.inlineShotMarks) return undefined;
    const scenario = el ? Number(el.dataset.bugScenario) : NaN;
    if (el && scenarios.some((item) => item.key === scenario))
      return {
        scenario,
        field: el.getAttribute(FIELD_ATTR) as FieldKey,
        pos: pos(el),
        el,
      };
    // Outside the fields (a button, a frame, the panel): at the caret of the field last typed in, if it is this scenario's.
    const last = lastField.current;
    if (last?.isConnected && Number(last.dataset.bugScenario) === owner) {
      return {
        scenario: owner,
        field: last.getAttribute(FIELD_ATTR) as FieldKey,
        pos: last.selectionStart,
        el: last,
      };
    }
    const expected = document.querySelector<HTMLTextAreaElement>(`textarea[${FIELD_ATTR}="expected"][data-bug-scenario="${owner}"]`);
    return { scenario: owner, field: 'expected', pos: 'end', el: expected };
  };

  /** Without "Файлы" only images are taken: the rest is turned down in words. */
  const takeFiles = (list: File[]) => {
    if (list.length === 0) return;
    if (filesOn) {
      addFiles(list);
      return;
    }
    const names = list.map((file) => `«${file.name || 'без имени'}»`).join(', ');
    setShotError(`Не картинка — не принято: ${names}. Прикладываются только снимки и картинки`);
  };

  const onPaste = (event: ClipboardEvent) => {
    const pasted = [...event.clipboardData.files];
    if (pasted.length === 0) return;
    event.preventDefault();
    const images = pasted.filter((file) => file.type.startsWith('image/'));
    if (options.inlineShotMarks)
      addImages(
        images,
        markAt(fieldOf(event.target), (el) => el.selectionStart),
        'paste',
      );
    else images.forEach((blob) => addImage(blob, 'paste'));
    takeFiles(pasted.filter((file) => !file.type.startsWith('image/')));
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const dropped = [...event.dataTransfer.files];
    if (!options.dropImagesAsShots && filesOn) {
      addFiles(dropped);
      return;
    }
    const images = dropped.filter((file) => file.type.startsWith('image/'));
    const { clientX, clientY } = event;
    addImages(
      images,
      markAt(fieldOf(event.target), (el) => dropOffset(el, clientX, clientY)),
      'drop',
    );
    takeFiles(dropped.filter((file) => !file.type.startsWith('image/')));
  };

  const missingExpected = !!options.requireExpected && scenarios.some((item) => !hasWords(item.answers.expected));

  const canSend =
    !sending &&
    !busyUploads &&
    !allShots.some((shot) => shot.status === 'capturing') &&
    !missingExpected &&
    // Quint manual_shot Sn2 (report 287): no report without a shot the person made (or put in: a file, a paste, a drop).
    readyCount > 0;
  const needsShot = readyCount === 0;

  const recording = !!trace?.recording;

  const pill = (minimized || (capturing && hidden)) && sentId === null && (
    <div className="bug-pill" role="status" {...OWN}>
      <Button variant="text" className="bug-pill-main" icon={<BugIcon />} onClick={onRestore} disabled={capturing} title="Развернуть баг-репорт">
        {plural(scenarios.length, SCENARIOS)} · {plural(readyCount, SHOTS)}
      </Button>
      {recording && (
        <span className="bug-pill-rec" title="Идёт запись">
          <span className="bug-rec-dot" aria-hidden />
          <LiveTrace>{(live) => clock(live.durationMs / 1000)}</LiveTrace>
        </span>
      )}
      <IconButton label="Снимок в текущий сценарий" size="sm" icon={<CameraIcon />} onClick={() => void addCapture()} busy={capturing} />
      <IconButton label={`Снимок области · ${AREA_HOTKEY}`} size="sm" icon={<CropIcon />} onClick={() => startArea()} disabled={capturing || picking} />
      <IconButton label="Развернуть" size="sm" icon={<PanelExpandIcon />} onClick={onRestore} disabled={capturing} />
    </div>
  );

  if (sentId !== null) {
    return (
      <aside className="bug-panel" role="dialog" aria-label="Баг-репорт" {...OWN}>
        <header className="bug-head">
          <span className="bug-head-icon">
            <BugIcon />
          </span>
          <span className="bug-head-title">Баг-репорт</span>
          <span className="bug-spacer" />
          <IconButton label="Закрыть" icon={<CloseIcon />} onClick={onClose} />
        </header>
        <div className="bug-body bug-sent">
          <span className="bug-sent-mark" aria-hidden>
            <CheckIcon />
          </span>
          <div className="bug-sent-title">Репорт №{sentId} отправлен</div>
          <div className="bug-sum">
            <span title="Сценариев">
              <Counter value={scenarios.length} tone="accent" />
            </span>
            <Metric icon={<CameraIcon />} value={readyCount} label="Снимков" />
            {files.length > 0 && <Metric icon={<PaperclipIcon />} value={files.length} label="Вложений" />}
            {withBodies && <Metric icon={<FileCodeIcon />} value="HTML" label="HTML страницы и тела ответов" />}
          </div>
          <div className="bug-sent-actions">
            <Button variant="primary" icon={<PlusIcon />} onClick={onAnother} autoFocus>
              Ещё репорт
            </Button>
            <Button onClick={onClose}>Готово</Button>
          </div>
        </div>
      </aside>
    );
  }

  // Нп3 (report 84): the shot tools sit in the footer by «Отправить» and feed the scenario worked with last (`activeRef`).
  const renderShots = (item: ScenarioItem, index: number) => {
    return (
      <div className="bug-shots" aria-label={`Снимки сценария ${index + 1}`}>
        {item.shots.map((s, i) => (
          <div key={s.key} className={`bug-thumb bug-thumb-${s.status}`}>
            {s.status === 'ready' && s.url ? (
              <button
                type="button"
                className="bug-thumb-open"
                onClick={() => setViewer({ scenario: item.key, shot: s.key })}
                aria-label={`Открыть снимок ${i + 1}`}
                title="Открыть — обвести рамкой"
              >
                <img src={s.url} alt="" draggable={false} />
              </button>
            ) : s.status === 'capturing' ? (
              <span className="bug-thumb-state">
                <Spinner size="sm" label="Снимаю" />
                Снимаю…
              </span>
            ) : (
              <span className="bug-thumb-state bug-thumb-fail" title={s.error}>
                <AlertIcon />
                Не вышел
              </span>
            )}
            <span className="bug-thumb-n">{i + 1}</span>
            {s.status === 'ready' && s.blocks && s.blocks.length > 0 && (
              <span className="bug-thumb-n-touch" title={`Затронуто: ${touchedText(s.blocks, maskKnown, 30)}`}>
                {touchedText(s.blocks, maskKnown, 2)}
              </span>
            )}
            {s.rects.length > 0 && (
              <span className="bug-thumb-marks" title="Рамок">
                <FrameIcon /> {s.rects.length}
              </span>
            )}
            {options.inlineShotMarks && s.status === 'ready' && (
              <IconButton
                label={`Метка снимка ${i + 1} в текст`}
                size="sm"
                icon={<MarkIcon />}
                className="bug-ib-onDark bug-thumb-x bug-thumb-mark"
                onClick={() => markAgain(item.key, i + 1)}
              />
            )}
            <IconButton
              label={s.status === 'capturing' ? 'Не ждать снимка' : `Убрать снимок ${i + 1}`}
              size="sm"
              icon={<CloseIcon />}
              className="bug-ib-onDark bug-thumb-x"
              onClick={() => removeShot(item.key, s.key)}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderScenario = (item: ScenarioItem, index: number) => {
    const open = !collapsed.has(item.key);
    const isActive = item.key === active.key;
    const ready = item.shots.filter((shot) => shot.status === 'ready').length;
    const live = traceOwner.current === item.key && trace;
    const peek = (hasWords(item.answers.expected) && item.answers.expected.trim()) || item.answers.did.trim();
    const answered = answeredCount(item.answers);
    return (
      <section
        key={item.key}
        className={`bug-sc${isActive ? ' bug-sc-on' : ''}`}
        aria-label={`Сценарий ${index + 1}`}
        onFocusCapture={() => setActiveKey(item.key)}
      >
        <Row
          className="bug-sc-head"
          // Ок1: the number of scenarios is in the header only — no number badge in the row.
          lead={<ChevronIcon open={open} />}
          title={
            <>
              <span className="bug-sc-title">Сценарий</span>
              {!open && peek && (
                <span className="bug-sc-peek" {...{ [PANEL_TEXT_ATTR]: '' }}>
                  {peek}
                </span>
              )}
            </>
          }
          end={
            <span className="bug-sc-meta">
              <span
                className="bug-progress"
                role="img"
                aria-label={`Отвечено ${answered} из ${REQUIRED_STEPS.length}`}
                title={`Отвечено ${answered} из ${REQUIRED_STEPS.length}`}
              >
                {REQUIRED_STEPS.map((step) => (
                  <span key={step.key} className={hasWords(item.answers[step.key]) ? 'bug-progress-on' : undefined} />
                ))}
              </span>
              {ready > 0 && <Metric icon={<CameraIcon />} value={ready} label="Снимков" />}
              {(item.traces.length > 0 || live) && (
                <Metric
                  icon={live && live.recording ? <span className="bug-rec-dot" aria-hidden /> : <TraceIcon />}
                  value={item.traces.length + (live ? 1 : 0)}
                  label="Записей"
                />
              )}
              {scenarios.length > 1 && (
                <IconButton
                  label={`Убрать сценарий ${index + 1}`}
                  size="sm"
                  variant="danger"
                  icon={<TrashIcon />}
                  onClick={() => void removeScenario(item.key)}
                />
              )}
            </span>
          }
          expanded={open}
          onClick={() => toggleOpen(item.key)}
        />
        {open && (
          <div className="bug-sc-body">
            <div className={`bug-did${item.answers.did ? '' : ' bug-did-empty'}`} title="Записано по вашим действиям — уйдёт строкой «Что делали»">
              <SparkIcon />
              <span className="bug-did-label">по действиям:</span>
              <span className="bug-did-text" {...{ [PANEL_TEXT_ATTR]: '' }}>
                {item.answers.did || 'действий нет'}
              </span>
            </div>
            {EDITABLE_STEPS.map((step) => {
              const key = step.key as FieldKey;
              const value = item.answers[key];
              const done = hasWords(value);
              const required = !!options.requireExpected && key === 'expected';
              const fieldId = `bug-${item.key}-${key}`;
              const label = `${SHORT[key]}, сценарий ${index + 1}`;
              return (
                <div key={key} className={`bug-field${done ? ' bug-field-done' : required ? ' bug-field-req' : ''}`}>
                  <label className="bug-field-label" htmlFor={fieldId}>
                    <span className="bug-field-mark" aria-hidden title={required && !done ? 'Обязательное' : undefined}>
                      {done ? <CheckIcon /> : null}
                    </span>
                    {SHORT[key]}
                  </label>
                  <TextArea
                    id={fieldId}
                    label={label}
                    hideLabel
                    {...{
                      [FIELD_ATTR]: key,
                      'data-bug-scenario': item.key,
                      [PANEL_TEXT_ATTR]: '',
                    }}
                    aria-required={required || undefined}
                    ref={index === 0 && key === 'expected' ? firstField : undefined}
                    aria-label={label}
                    maxRows={8}
                    value={value}
                    onChange={(event) => setAnswer(item.key, key, event.target.value, event.currentTarget)}
                    onFocus={(event) => {
                      lastField.current = event.currentTarget;
                    }}
                    maxLength={6000}
                  />
                </div>
              );
            })}

            {options.rephrase && <RephraseAnswer rephrase={rephrase} scenario={item.key} index={index} />}

            {renderShots(item, index)}

            {(item.traces.length > 0 || live) && (
              <div className="bug-rows" role="list" aria-label={`Записи сценария ${index + 1}`}>
                {item.traces.map((file, i) => (
                  <div key={i} role="listitem">
                    <Row
                      lead={<TraceIcon />}
                      title={`Запись · ${traceLabel(file.events.length, file.durationMs)}`}
                      wrap
                      end={
                        <IconButton
                          label="Выбросить запись"
                          size="sm"
                          icon={<CloseIcon />}
                          onClick={() =>
                            updateScenario(item.key, (s) => ({
                              ...s,
                              traces: s.traces.filter((t) => t !== file),
                            }))
                          }
                        />
                      }
                    />
                  </div>
                ))}
                {live && (
                  <div role="listitem">
                    <Row
                      lead={<span className="bug-rec-dot" aria-hidden />}
                      title={<LiveTrace>{(status) => `Идёт запись · ${traceLabel(status.events, status.durationMs)}`}</LiveTrace>}
                      wrap
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    );
  };

  const viewed = (() => {
    if (!viewer) return null;
    const item = scenarios.find((s) => s.key === viewer.scenario);
    const list = item?.shots.filter((shot) => shot.status === 'ready' && shot.url) ?? [];
    const at = list.findIndex((shot) => shot.key === viewer.shot);
    if (!item || at < 0) return null;
    return { item, list, at, shot: list[at], n: scenarios.indexOf(item) + 1 };
  })();

  const lightbox = viewed && !hidden && (
    <div className="bug-viewer" role="dialog" aria-modal="true" aria-label="Снимок" {...OWN}>
      <div className="bug-viewer-bar">
        <Counter value={viewed.n} strong className="bug-viewer-n" />
        <span className="bug-viewer-title">
          Снимок {viewed.at + 1} из {viewed.list.length}
        </span>
        <span className="bug-spacer" />
        <IconButton
          label="Убрать последнюю рамку"
          icon={<UndoIcon />}
          className="bug-ib-onDark"
          disabled={viewed.shot.rects.length === 0}
          onClick={() => setRects(viewed.item.key, viewed.shot.key, viewed.shot.rects.slice(0, -1))}
        />
        <IconButton
          label="Предыдущий снимок"
          icon={<icons.IconChevronLeft />}
          className="bug-ib-onDark"
          disabled={viewed.at === 0}
          onClick={() =>
            setViewer({
              scenario: viewed.item.key,
              shot: viewed.list[viewed.at - 1].key,
            })
          }
        />
        <IconButton
          label="Следующий снимок"
          icon={<icons.IconChevronRight />}
          className="bug-ib-onDark"
          disabled={viewed.at === viewed.list.length - 1}
          onClick={() =>
            setViewer({
              scenario: viewed.item.key,
              shot: viewed.list[viewed.at + 1].key,
            })
          }
        />
        <IconButton label="Убрать снимок" icon={<TrashIcon />} className="bug-ib-onDark" onClick={() => removeShot(viewed.item.key, viewed.shot.key)} />
        <IconButton label="Закрыть (Esc)" icon={<CloseIcon />} className="bug-ib-onDark" onClick={() => setViewer(null)} autoFocus />
      </div>
      {viewed.shot.blocks && viewed.shot.blocks.length > 0 && (
        <div className="bug-viewer-touch" {...{ [PANEL_TEXT_ATTR]: '' }}>
          <span className="bug-viewer-touch-label">Затронуто:</span> {touchedText(viewed.shot.blocks, maskKnown, 12)}
        </div>
      )}
      <div className="bug-viewer-stage">
        <ShotEditor src={viewed.shot.url!} rects={viewed.shot.rects} onChange={(rects) => setRects(viewed.item.key, viewed.shot.key, rects)} />
      </div>
    </div>
  );


  return (
    <>
      <aside
        className={`bug-panel${dragging ? ' bug-panel-drop' : ''}${resizing ? ' bug-panel-resizing' : ''}`}
        role="dialog"
        aria-label="Баг-репорт"
        {...OWN}
        style={hidden ? { display: 'none' } : undefined}
        onPaste={onPaste}
        onDragOver={(event) => {
          if ([...event.dataTransfer.types].includes('Files')) {
            event.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={onDrop}
      >
        <div
          className="bug-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Ширина панели"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          aria-valuenow={clampWidth(width)}
          tabIndex={0}
          title="Потяните, чтобы изменить ширину"
          onPointerDown={startResize}
          onPointerMove={moveResize}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          onKeyDown={keyResize}
          onDoubleClick={() => {
            setWidth(DEFAULT_WIDTH);
            saveWidth(DEFAULT_WIDTH);
          }}
        />

        <header className="bug-head">
          <span className="bug-head-icon">
            <BugIcon />
          </span>
          <span className="bug-head-title">Баг-репорт</span>
          <Badge tone="accent" title="Сценариев">
            {plural(scenarios.length, SCENARIOS)}
          </Badge>
          <span className="bug-spacer" />
          <IconButton label="Свернуть — черновик останется" icon={<PanelCollapseIcon />} onClick={onMinimize} />
          <IconButton label="Закрыть без отправки" icon={<CloseIcon />} onClick={() => void close()} disabled={sending} />
        </header>

        <div className="bug-body">
          {seeds.map((seed, index) => (
            <section key={seed.id ?? `q${index}`} className="bug-from-q" aria-label="Из вопроса">
              <div className="bug-from-q-head">
                <HelpIcon />
                <span>Из вопроса{seed.id ? ` №${seed.id}` : ''}</span>
                <span className="bug-from-q-place" title="Место">
                  {seed.place}
                </span>
              </div>
              <p className="bug-from-q-text" {...{ [PANEL_TEXT_ATTR]: '' }}>
                {seed.question}
              </p>
              <p className="bug-from-q-text bug-from-q-answer" {...{ [PANEL_TEXT_ATTR]: '' }}>
                <b>{seed.found ? 'Есть в коде' : 'Предположение'}:</b> {seed.answer || 'нет'}
              </p>
            </section>
          ))}
          {scenarios.map(renderScenario)}
          {shotError && (
            <Banner tone="error" icon={<AlertIcon />} className="bug-alert">
              {shotError}
            </Banner>
          )}
          <input
            ref={shotInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(event) => {
              const picked = [...(event.target.files ?? [])];
              if (options.inlineShotMarks)
                addImages(
                  picked,
                  markAt(null, () => 0),
                );
              else picked.forEach((blob) => addImage(blob));
              event.target.value = '';
            }}
          />

          {filesOn && (
            <div className="bug-folds">


              {filesOn && (
                <Fold title="Файлы" open={sections.has('file')} onToggle={() => toggleSection('file')}>
                  <Toolbar dense>
                    <Button icon={<PaperclipIcon />} onClick={() => fileInput.current?.click()}>
                      Выбрать
                    </Button>
                    <span className="muted small">или перетащите / вставьте</span>
                  </Toolbar>
                  {files.length > 0 && (
                    <div className="bug-rows" role="list">
                      {files.map((f) => (
                        <div key={`f${f.key}`} role="listitem">
                          <Row
                            className={f.status === 'error' ? 'bug-row-error' : undefined}
                            lead={f.status === 'uploading' ? <Spinner size="sm" label="Загружаю" /> : f.status === 'error' ? <AlertIcon /> : <PaperclipIcon />}
                            title={`${f.name} · ${sizeText(f.size)}${f.status === 'error' ? ` — ${f.error}` : ''}`}
                            wrap
                            end={
                              f.status !== 'uploading' && (
                                <IconButton
                                  label={`Убрать ${f.name}`}
                                  size="sm"
                                  icon={<CloseIcon />}
                                  onClick={() => {
                                    if (f.upload) void bugApi.discard(f.upload.id).catch(() => undefined);
                                    setFiles((prev) => prev.filter((item) => item.key !== f.key));
                                  }}
                                />
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </Fold>
              )}
            </div>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              addFiles([...(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />

        </div>

        <div className="bug-toasts" ref={setToastBox} />
        {toastBox && removed && (
          <Toast
            container={toastBox}
            className="bug-toast"
            ms={0}
            text="Снимок убран"
            action={{ label: 'Вернуть', run: restoreShot }}
            onClose={() => setRemoved(null)}
          />
        )}
        {error && (
          <Banner tone="error" icon={<AlertIcon />} className="bug-alert bug-alert-foot">
            {error}
          </Banner>
        )}
        <footer className="bug-foot">
          {/* Нп3 (report 84): the shot tools — by the send button, like the support widget at the bottom; Ок5: the shield is among them. */}
          <span className="bug-tools" aria-label="Снимок">
            <IconButton label="Снимок страницы" icon={<CameraIcon />} onClick={() => void addCapture()} busy={capturing} disabled={capturing || picking} />
            <IconButton
              label={`Снимок области — выделите часть экрана · ${AREA_HOTKEY}`}
              icon={<CropIcon />}
              onClick={() => startArea()}
              disabled={capturing || picking}
            />
            <ScreenMaskBeacon offOpensShots={options.maskSwitch} />
          </span>
          {/* Рф8: «Переформулировать» — by the shot tools and «Отправить»; asks about the scenario worked with last (Нп4). */}
          {options.rephrase && (
            <span className="bug-foot-ask">
              <RephraseButton rephrase={rephrase} scenarioKey={active.key} disabled={sending || sentId !== null} />
            </span>
          )}
          {/* Нп2: «HTML страницы уйдёт» — a compact icon with the tip, no bare word (Вп6). */}
          {withBodies && (
            <span
              className="bug-html-tip"
              role="img"
              aria-label="HTML страницы и тела ответов уйдут вместе с репортом, имена в них скрыты"
              title="HTML страницы и тела ответов уйдут вместе с репортом, имена в них скрыты"
            >
              <FileCodeIcon />
            </span>
          )}
          {needsShot && (
            <span className="bug-foot-why" role="status" id="bug-send-why">
              <CameraIcon />
              Сделайте снимок экрана — без него репорт не отправить
            </span>
          )}
          <Button
            variant="primary"
            className="bug-send"
            icon={<SendIcon />}
            busy={sending}
            disabled={!canSend}
            aria-describedby={needsShot ? 'bug-send-why' : undefined}
            onClick={() => void submit()}
          >
            {sending ? 'Отправляю…' : 'Отправить'}
          </Button>
        </footer>
      </aside>
      {lightbox}
      {pill}
      {picking && <AreaPicker onPick={pickArea} onCancel={cancelArea} />}
      {confirmDialog}
    </>
  );
}
